
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Typography, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { FC } from 'react';
import { FerrureBox } from '../../../../core/FerrureBox';
import NumberCircle from '../../../../core/NumberCircle';
import { OeillereBox } from '../../../../core/OeillereBox';
import { formatMusique, formatReduction, getPoidHandicapLabel } from '../../../../utils/format';
import { SORT_OPTION } from '../../services/enum';
import { Prono } from '../../services/type';
import { EngagementRow } from './EngagementRow';
import { ScoreComponent } from './ScoreComponent';

interface Props {
  prono: Prono;
  isOnPodium: boolean;
  sortSelected: SORT_OPTION;
  isMarked: boolean;
  onClick: (num: number) => void;
  plafond?: number;
  recul?: number;
}

const PronoCard: FC<Props> = ({
  prono,
  isOnPodium,
  sortSelected,
  onClick,
  isMarked,
  plafond,
  recul,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const isFerreChanged =
    (prono?.reports?.length || 0) > 0 &&
    prono.data.deferre !== prono.reports[0].deferre;
  const isOeillereChanged =
    (prono?.reports?.length || 0) > 0 &&
    prono.data.oeilleres !== prono.reports[0].oeilleres;

  const header = (
    <NumberCircle numero={prono.data.numero} size={isMobile ? 54 : 64}>
      {(prono?.preparation?.bonusSupplement || 0) > 0 && (
        <Typography
          color="secondary"
          variant="subtitle2"
          fontSize={14}
          fontWeight={600}
          sx={{
            position: 'absolute',
            bottom: -20,
            left: isMobile ? 7 : 13,
          }}
        >
          {`Suppl.`}
        </Typography>
      )}
      {isOnPodium && (
        <EmojiEventsIcon
          sx={{
            position: 'absolute',
            bottom: -8,
            right: -4,
            fontSize: 22,
            color: isMarked ? 'primary.main' : 'white',
            bgcolor: 'secondary.main',
            borderRadius: '50%',
            p: '2px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      )}
    </NumberCircle>
  );

  const content = (
    <Box
      display={'flex'}
      flexDirection={'column'}
      color={'primary.main'}
      alignItems={'flex-start'}
      justifyContent={'flex-start'}
      flexGrow={1}
    >
      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'baseline'}
        gap={isMobile ? 0 : 1}
        position={'relative'}
      >
        <Typography
          maxWidth={isMobile ? '112px' : '350px'}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          fontWeight={600}
          fontSize={isMobile ? 18 : 22}
        >
          {prono.data.nom}
        </Typography>
        <Box position={'absolute'} right={isMobile ? -40 : -60} top={0}>
          <FerrureBox
            deferre={prono.data.deferre}
            isSecondary={isFerreChanged && !isMarked}
          />
        </Box>
        <OeillereBox
          oeillere={prono.data.oeilleres}
          isSecondary={isOeillereChanged}
        />
      </Box>
      <Typography
        variant="subtitle2"
        fontSize={isMobile ? 12 : 14}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: isMobile ? '142px' : '350px',
        }}
      >
        {`${prono.data.jockey} / ${prono.data.entraineur}`}
      </Typography>
      <Box display={'flex'} flexDirection={'row'} gap={1}>
        <Typography variant="subtitle2" fontSize={isMobile ? 12 : 14}>
          {isMobile
            ? `${prono.data.sexe.slice(0, 1).toUpperCase()} - ${prono.data.age} ans`
            : `${prono.data.sexe.toLowerCase()} - ${prono.data.age} ans`}
        </Typography>
        <Typography variant="subtitle2" fontSize={isMobile ? 12 : 14}>
          {getPoidHandicapLabel(prono.data.handicap, prono.data.poids)}
        </Typography>
      </Box>
      <Box display={'flex'} flexDirection={'row'} gap={1}>
        <Typography variant="subtitle2" fontSize={isMobile ? 12 : 14}>
          {formatMusique(prono.data.musique, isMobile)}
        </Typography>
      </Box>
      <EngagementRow
        gainsCheval={prono.data.price}
        plafond={plafond}
        recul={recul}
      // averageRed={prono.performance.averageRed}
      />
    </Box>
  );

  const right = (
    <Box display="flex" flexDirection="column" alignItems="flex-end" justifyContent="space-between" height="100%">
      <ScoreComponent prono={prono} sortSelected={sortSelected} />

      {prono.performance.averageRed != null && prono.performance.averageRed > 0 && (
        <Typography
          variant="caption"
          fontSize={isMobile ? 11 : 12}
          color="text.secondary"
          mt={0.5}
        >
          {`red. moyenne : `}
          <Typography
            component="span"
            variant="caption"
            fontSize={isMobile ? 11 : 12}
            fontWeight={sortSelected === SORT_OPTION.RED ? 700 : 500}
            color={sortSelected === SORT_OPTION.RED ? "primary.main" : "text.secondary"}
          >
            {formatReduction(prono.performance.averageRed)}
          </Typography>
        </Typography>
      )}
    </Box>
  );

  const AXIS_COLORS: Record<string, string> = {
    Performance: '#3962BD',
    Contexte: '#694DC1',
    Préparation: '#2D8D98',
  };

  const axisBar = (
    label: string,
    value: number
  ) => (
    <Box display="flex" flexDirection="column" gap={0.3} flex={1}>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={12} color="text.secondary">
          {label}
        </Typography>
        <Typography fontSize={14} fontWeight={700} sx={{ color: AXIS_COLORS[label] }}>
          {value.toFixed(1)}
        </Typography>
      </Box>
      <Box
        height={8}
        bgcolor="grey.400"
        border="1px solid"
        borderColor="divider"
        borderRadius={99}
        overflow="hidden"
      >
        <Box
          height="100%"
          width={`${Math.min(100, Math.max(0, value * 10))}%`}
          bgcolor={AXIS_COLORS[label]}
          borderRadius={99}
          sx={{ transition: 'width 0.3s ease' }}
        />
      </Box>
    </Box>
  );

  const axes = (
    <Box
      display="flex"
      flexDirection="row"
      gap={2}
      mt={1}
      pt={1}
      borderTop="0.5px solid"
      borderColor="divider"
      width="100%"
    >
      {axisBar('Performance', prono.performance.scoreTotal || 0)}
      {axisBar('Contexte', prono.context.scoreTotal || 0)}
      {axisBar('Préparation', prono.preparation.scoreTotal || 0)}
    </Box>
  );

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      sx={{
        boxShadow: '0px 4px 21px 4px rgba(0,0,0, 0.25)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0px 8px 25px 0px rgba(0,0,0, 0.3)',
        },
        cursor: 'pointer',
      }}
      width={isMobile ? '95%' : '80%'}
      bgcolor={isMarked ? 'secondary.light' : 'white'}
      borderRadius={'16px'}
      p={isMobile ? 0.5 : 1}
    >
      <Box
        display={'flex'}
        flexDirection={'row'}
        onClick={() => onClick(prono.data.numero)}
        gap={isMobile ? 1 : 2}
        width={'100%'}
        position="relative"
        alignItems="flex-start"
      >
        {header}
        {content}
        {right}
      </Box>
      <Box px={isMobile ? 1 : 2} pb={0.5}>
        {axes}
      </Box>
    </Box>
  );
};

export default PronoCard;
